/**
 * SOLID Review
 * S — Strong. sole job: implement IAdapterApolloRoom's room-lifecycle/subscription operations against the
 *     real backend via Apollo's imperative client API. Strong for the methods it currently has
 *     (leaveRoom — Phase 1b Group A; subscribeToPlayerSuspended/subscribeToPlayerResumed/
 *     subscribeToRoomAbandoned/subscribeToDecisionCountdown — Phase 1b Group B; getRoomByCode/
 *     getActiveRoomForPlayer — Group C2, via `client.query()`; joinRoom/lateJoinRoom/
 *     rejoinRoomByCode — Group C3, via `client.mutate()`, completing the smart-join routing chain;
 *     getRoom/subscribeToRoomUpdated — Phase 2 Group B). createRoom/startGame were deleted as
 *     housekeeping — fabricated placeholders with zero known callers anywhere in the tree,
 *     confirmed after Phase 2 completed. getLiveKitToken/markMediaPresence/
 *     reportLiveKitConnectionState/getLiveKitHealth/getLiveKitUsageReport (Phase 1b Group A,
 *     Phase 2 Groups C/E) moved onto a real IAdapterApolloLiveKit port
 *     (infrastructure/livekit/ApolloLiveKitAdapter.ts) as part of the ISP fix
 *     (plan/plan_frontend_makeover.md) — they were LiveKit concerns co-located here, not
 *     room-lifecycle operations.
 * O — Good. A new room-lifecycle operation is a new method here plus a new IAdapterApolloRoom signature —
 *     mechanical, no existing method touched.
 * L — N/A — no inheritance hierarchy; implements IAdapterApolloRoom directly.
 * I — Strong. Implements exactly the room-lifecycle/subscription interface it needs, nothing else — no
 *     longer bundled alongside LiveKit signaling/admin operations on a shared port.
 * D — Strong. Takes ApolloClient as a constructor-injected dependency rather than importing a singleton —
 *     testable without a real network. The 4 subscribe methods deliberately relay raw server events
 *     unfiltered — the room-scoping defense-in-depth guard for playerSuspended/
 *     playerResumed/roomAbandoned stays in AppRoot.tsx, unchanged from before this migration (see
 *     plan/plan_frontend_makeover.md's Phase 1b Group B entry for why). joinRoom/lateJoinRoom/
 *     rejoinRoomByCode are equally thin — the REJOIN identity-swap logic (adopting the
 *     server-authoritative playerId on a suspended-slot match) stays in AppRoot.tsx too, operating on this
 *     adapter's plain `Result`-unwrapped response.
 */
import { gql } from '@apollo/client';
import type { Result } from '../../shared/Result';
import type { ICommandLeaveRoom, IEntityRoomCodeLookup, IEntityActiveRoomForPlayer, IEntityRawRoom, IEntityRejoinRoomByCode } from '../../application/ports/apolloShapes';
import type { RoomId, PlayerId } from '../../application/ports/ClientVocabulary';
import { ok, fail } from '../../shared/Result';
import type { IAdapterApolloRoom } from '../../application/ports/IAdapterApolloRoom';
import type { IPortGraphQLDomainClient } from '../contract/ports';
import type { IEntityTableView } from '../../application/ports/IAdapterApolloRoom';

// ---------------------------------------------------------------------------
// GraphQL operations (Group A). Moved here from AppRoot.tsx's inline definitions.
// ---------------------------------------------------------------------------

const LEAVE_ROOM_MUTATION = gql`
  mutation ApolloRoomAdapter_LeaveRoom($roomId: ID!, $playerId: ID!) {
    leaveRoom(roomId: $roomId, playerId: $playerId)
  }
`;

const PLAYER_SUSPENDED_SUB = gql`
  subscription ApolloRoomAdapter_PlayerSuspended($roomId: ID!) {
    playerSuspended(roomId: $roomId) { playerId roomId isSuspended }
  }
`;

const PLAYER_RESUMED_SUB = gql`
  subscription ApolloRoomAdapter_PlayerResumed($roomId: ID!) {
    playerResumed(roomId: $roomId) { playerId roomId isSuspended }
  }
`;

const ROOM_ABANDONED_SUB = gql`
  subscription ApolloRoomAdapter_RoomAbandoned($roomId: ID!) {
    roomAbandoned(roomId: $roomId) { roomId reason }
  }
`;

const DECISION_COUNTDOWN_SUB = gql`
  subscription ApolloRoomAdapter_DecisionCountdown($roomId: ID!) {
    decisionCountdown(roomId: $roomId) { roundId challengerId secondsRemaining }
  }
`;

const TABLE_VIEW_SUB = gql`
  subscription ApolloRoomAdapter_TableView($roomId: ID!) {
    tableView(roomId: $roomId) {
      roomId version status suspendedPlayerIds
      activeRound {
        roundId rollerId diceRolled minimumScore
        pendingClaim { claimedScore label challengerId wasBlind }
        playerLives { playerId lives }
        revealResult { losingPlayerId wasBluff claimedScore trueScore die1 die2 newRollerId endsGame loserLivesLeft }
      }
    }
  }
`;

const GET_ROOM_BY_CODE_QUERY = gql`
  query ApolloRoomAdapter_GetRoomByCode($code: String!) {
    getRoomByCode(code: $code) {
      roomId roomStatus playerCount
      suspendedPlayers { id displayName email isConnected }
    }
  }
`;

const GET_ACTIVE_ROOM_FOR_PLAYER_QUERY = gql`
  query ApolloRoomAdapter_GetActiveRoomForPlayer($playerId: ID!) {
    getActiveRoomForPlayer(playerId: $playerId) {
      roomId roomCode roomStatus playerCount
    }
  }
`;

const CREATE_ROOM_MUTATION = gql`
  mutation ApolloRoomAdapter_CreateRoom($hostPlayerId: ID!) {
    createRoom(hostPlayerId: $hostPlayerId) {
      id roomCode playerIds
      players { id displayName }
      status
    }
  }
`;

const JOIN_ROOM_MUTATION = gql`
  mutation ApolloRoomAdapter_JoinRoom($roomId: ID!, $playerId: ID!) {
    joinRoom(roomId: $roomId, playerId: $playerId) {
      id roomCode playerIds players { id displayName } status
    }
  }
`;

const LATE_JOIN_ROOM_MUTATION = gql`
  mutation ApolloRoomAdapter_LateJoinRoom($roomId: ID!, $playerId: ID!, $playerDisplayName: String!, $playerMatchCredits: Int) {
    lateJoinRoom(roomId: $roomId, playerId: $playerId, playerDisplayName: $playerDisplayName, playerMatchCredits: $playerMatchCredits) {
      id roomCode playerIds players { id displayName } status
    }
  }
`;

const REJOIN_BY_CODE_MUTATION = gql`
  mutation ApolloRoomAdapter_RejoinByCode($roomCode: String!, $playerId: ID!, $playerDisplayName: String!, $playerEmail: String) {
    rejoinRoomByCode(roomCode: $roomCode, playerId: $playerId, playerDisplayName: $playerDisplayName, playerEmail: $playerEmail) {
      playerId roomId roomCode roomStatus players { id displayName }
    }
  }
`;

const GET_ROOM_QUERY = gql`
  query ApolloRoomAdapter_GetRoom($id: ID!) {
    room(id: $id) {
      id roomCode playerIds players { id displayName } status
    }
  }
`;

const ROOM_UPDATED_SUB = gql`
  subscription ApolloRoomAdapter_RoomUpdated($roomId: ID!) {
    roomUpdated(roomId: $roomId) {
      id roomCode playerIds players { id displayName } status
    }
  }
`;

export class ApolloRoomAdapter implements IAdapterApolloRoom {
  // The executor arrives from this domain's socket on ComponentFrontendInfrastructure, not as
  // the raw ApolloClient. That socket is the single path from "this app can reach the domain"
  // to "this adapter executes" — disconnect it and this adapter degrades to the Null client
  // instead of quietly using a module singleton (§28.72).
  constructor(
    private readonly client: IPortGraphQLDomainClient,
  ) {}

  async getRoom(roomId: RoomId): Promise<Result<IEntityRawRoom>> {
    try {
      const { data, error } = await this.client.query<{ room: IEntityRawRoom | null }>({
        query: GET_ROOM_QUERY,
        variables: { id: roomId },
        fetchPolicy: 'network-only',
      });
      if (error) {
        return fail(new Error(`ApolloRoomAdapter.getRoom returned a GraphQL error: ${error.message}`));
      }
      if (!data?.room) {
        return fail(new Error('ApolloRoomAdapter.getRoom returned no data'));
      }
      return ok(data.room);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error fetching room';
      return fail(new Error(`ApolloRoomAdapter.getRoom failed: ${message}`));
    }
  }

  async getRoomByCode(code: string): Promise<Result<IEntityRoomCodeLookup | null>> {
    try {
      const { data, error } = await this.client.query<{ getRoomByCode: IEntityRoomCodeLookup | null }>({
        query: GET_ROOM_BY_CODE_QUERY,
        variables: { code },
        fetchPolicy: 'network-only',
      });
      if (error) {
        return fail(new Error(`ApolloRoomAdapter.getRoomByCode returned a GraphQL error: ${error.message}`));
      }
      return ok(data?.getRoomByCode ?? null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error looking up room by code';
      return fail(new Error(`ApolloRoomAdapter.getRoomByCode failed: ${message}`));
    }
  }

  async getActiveRoomForPlayer(playerId: PlayerId): Promise<Result<IEntityActiveRoomForPlayer | null>> {
    try {
      const { data, error } = await this.client.query<{ getActiveRoomForPlayer: IEntityActiveRoomForPlayer | null }>({
        query: GET_ACTIVE_ROOM_FOR_PLAYER_QUERY,
        variables: { playerId },
        fetchPolicy: 'network-only',
      });
      if (error) {
        return fail(new Error(`ApolloRoomAdapter.getActiveRoomForPlayer returned a GraphQL error: ${error.message}`));
      }
      return ok(data?.getActiveRoomForPlayer ?? null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error looking up active room for player';
      return fail(new Error(`ApolloRoomAdapter.getActiveRoomForPlayer failed: ${message}`));
    }
  }

  /**
   * MOVED OUT OF MainMenuPanel.tsx (2026-08-30), where it was an inline `gql` plus a `useMutation`.
   * IAdapterApolloRoom's own comment recorded that the real flow went "entirely bypassing this
   * port" — a live bypass described rather than fixed. The document is byte-identical apart from
   * the operation name, which follows this adapter's convention.
   */
  async createRoom(hostPlayerId: PlayerId): Promise<Result<IEntityRawRoom>> {
    try {
      const { data, error } = await this.client.mutate<{ createRoom: IEntityRawRoom }>({
        mutation: CREATE_ROOM_MUTATION,
        variables: { hostPlayerId },
      });
      if (error) {
        return fail(new Error(`ApolloRoomAdapter.createRoom returned a GraphQL error: ${error.message}`));
      }
      if (!data?.createRoom) {
        return fail(new Error('ApolloRoomAdapter.createRoom returned no data'));
      }
      return ok(data.createRoom);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error creating room';
      return fail(new Error(`ApolloRoomAdapter.createRoom failed: ${message}`));
    }
  }

  async joinRoom(roomId: RoomId, playerId: PlayerId): Promise<Result<IEntityRawRoom>> {
    try {
      const { data, error } = await this.client.mutate<{ joinRoom: IEntityRawRoom }>({
        mutation: JOIN_ROOM_MUTATION,
        variables: { roomId, playerId },
      });
      if (error) {
        return fail(new Error(`ApolloRoomAdapter.joinRoom returned a GraphQL error: ${error.message}`));
      }
      if (!data?.joinRoom) {
        return fail(new Error('ApolloRoomAdapter.joinRoom returned no data'));
      }
      return ok(data.joinRoom);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error joining room';
      return fail(new Error(`ApolloRoomAdapter.joinRoom failed: ${message}`));
    }
  }

  async lateJoinRoom(roomId: RoomId, playerId: PlayerId, playerDisplayName: string, playerMatchCredits: number): Promise<Result<IEntityRawRoom>> {
    try {
      const { data, error } = await this.client.mutate<{ lateJoinRoom: IEntityRawRoom }>({
        mutation: LATE_JOIN_ROOM_MUTATION,
        variables: { roomId, playerId, playerDisplayName, playerMatchCredits },
      });
      if (error) {
        return fail(new Error(`ApolloRoomAdapter.lateJoinRoom returned a GraphQL error: ${error.message}`));
      }
      if (!data?.lateJoinRoom) {
        return fail(new Error('ApolloRoomAdapter.lateJoinRoom returned no data'));
      }
      return ok(data.lateJoinRoom);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error late-joining room';
      return fail(new Error(`ApolloRoomAdapter.lateJoinRoom failed: ${message}`));
    }
  }

  async rejoinRoomByCode(roomCode: string, playerId: PlayerId, playerDisplayName: string, playerEmail: string | null): Promise<Result<IEntityRejoinRoomByCode>> {
    try {
      const { data, error } = await this.client.mutate<{ rejoinRoomByCode: IEntityRejoinRoomByCode }>({
        mutation: REJOIN_BY_CODE_MUTATION,
        variables: { roomCode, playerId, playerDisplayName, playerEmail: playerEmail ?? undefined },
      });
      if (error) {
        return fail(new Error(`ApolloRoomAdapter.rejoinRoomByCode returned a GraphQL error: ${error.message}`));
      }
      if (!data?.rejoinRoomByCode) {
        return fail(new Error('ApolloRoomAdapter.rejoinRoomByCode returned no data'));
      }
      return ok(data.rejoinRoomByCode);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error rejoining room by code';
      return fail(new Error(`ApolloRoomAdapter.rejoinRoomByCode failed: ${message}`));
    }
  }

  async leaveRoom(command: ICommandLeaveRoom): Promise<Result<void>> {
    try {
      const { error } = await this.client.mutate({
        mutation: LEAVE_ROOM_MUTATION,
        variables: { roomId: command.roomId, playerId: command.playerId },
      });
      if (error) {
        return fail(new Error(`ApolloRoomAdapter.leaveRoom returned a GraphQL error: ${error.message}`));
      }
      return ok(undefined);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error leaving room';
      return fail(new Error(`ApolloRoomAdapter.leaveRoom failed: ${message}`));
    }
  }

  subscribeToPlayerSuspended(roomId: RoomId, onEvent: (ev: { playerId: PlayerId; roomId: RoomId }) => void): () => void {
    const sub = this.client.subscribe<{ playerSuspended: { playerId: PlayerId; roomId: RoomId; isSuspended: boolean } }>({ query: PLAYER_SUSPENDED_SUB, variables: { roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.playerSuspended) onEvent(data.playerSuspended);
      },
      error: (err: unknown) => console.warn('[ApolloRoomAdapter] playerSuspended subscription error:', err),
    });
    return () => sub.unsubscribe();
  }

  subscribeToPlayerResumed(roomId: RoomId, onEvent: (ev: { playerId: PlayerId; roomId: RoomId }) => void): () => void {
    const sub = this.client.subscribe<{ playerResumed: { playerId: PlayerId; roomId: RoomId; isSuspended: boolean } }>({ query: PLAYER_RESUMED_SUB, variables: { roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.playerResumed) onEvent(data.playerResumed);
      },
      error: (err: unknown) => console.warn('[ApolloRoomAdapter] playerResumed subscription error:', err),
    });
    return () => sub.unsubscribe();
  }

  subscribeToRoomAbandoned(roomId: RoomId, onEvent: (ev: { roomId: RoomId; reason: string }) => void): () => void {
    const sub = this.client.subscribe<{ roomAbandoned: { roomId: RoomId; reason: string } }>({ query: ROOM_ABANDONED_SUB, variables: { roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.roomAbandoned) onEvent(data.roomAbandoned);
      },
      error: (err: unknown) => console.warn('[ApolloRoomAdapter] roomAbandoned subscription error:', err),
    });
    return () => sub.unsubscribe();
  }

  subscribeToDecisionCountdown(roomId: RoomId, onEvent: (ev: { roundId: string; challengerId: string; secondsRemaining: number }) => void): () => void {
    const sub = this.client.subscribe<{ decisionCountdown: { roundId: string; challengerId: string; secondsRemaining: number } }>({ query: DECISION_COUNTDOWN_SUB, variables: { roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.decisionCountdown) onEvent(data.decisionCountdown);
      },
      error: (err: unknown) => console.warn('[ApolloRoomAdapter] decisionCountdown subscription error:', err),
    });
    return () => sub.unsubscribe();
  }

  subscribeToTableView(roomId: RoomId, onEvent: (view: IEntityTableView) => void): () => void {
    const sub = this.client.subscribe<{ tableView: IEntityTableView }>({ query: TABLE_VIEW_SUB, variables: { roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.tableView) onEvent(data.tableView);
      },
      error: (err: unknown) => console.warn('[ApolloRoomAdapter] tableView subscription error:', err),
    });
    return () => sub.unsubscribe();
  }

  subscribeToRoomUpdated(roomId: RoomId, onEvent: (ev: IEntityRawRoom) => void): () => void {
    const sub = this.client.subscribe<{ roomUpdated: IEntityRawRoom }>({ query: ROOM_UPDATED_SUB, variables: { roomId } }).subscribe({
      next: ({ data }) => {
        if (data?.roomUpdated) onEvent(data.roomUpdated);
      },
      error: (err: unknown) => console.warn('[ApolloRoomAdapter] roomUpdated subscription error:', err),
    });
    return () => sub.unsubscribe();
  }

}
